import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Sales, Tasks, Spares, Files } from "../lib/api";
import { getUploadPolicy, validateUpload } from "../lib/uploadPolicy";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Save, FileCheck, Package, CheckCircle2, XCircle, AlertTriangle,
  Upload, FileText, Eye, Trash2, Clock,
} from "lucide-react";
import { cn } from "../lib/utils";
import { buildDeliveryDateTime, getTimeRemaining, useTicker } from "../lib/time";
import { BillingLedgerBanner } from "../components/BillingLedgerBanner";

function TimerBanner({ deliveryAt }) {
  if (!deliveryAt) return null;
  const t = getTimeRemaining(deliveryAt);
  const tones = {
    success: "bg-emerald-50 text-emerald-800 border-emerald-200",
    warning: "bg-amber-50 text-amber-800 border-amber-200",
    danger: "bg-rose-50 text-rose-800 border-rose-200",
    neutral: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <span className={cn(
      "inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 font-mono-data text-xs font-semibold",
      tones[t.tone],
    )}>
      <Clock className="h-3.5 w-3.5" />
      {t.overdue ? `Overdue ${t.label}` : `Time remaining ${t.label}`}
    </span>
  );
}

function ContextSection({ sale, loc }) {
  const row = (label, value) => (
    <div>
      <dt className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-900">{value || <span className="text-slate-400">—</span>}</dd>
    </div>
  );
  return (
    <Card className="border-slate-200 p-5">
      <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">Sale Context · Read-only</div>
      <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
        {row("SR Number", <span className="font-mono-data">{sale.sr_number}</span>)}
        {row("Customer", sale.customer?.name)}
        {row("Mobile", sale.customer?.phone)}
        {row("Location", loc ? `${loc.name} (${loc.code})` : "—")}
        {row("Model", sale.vehicle?.model)}
        {row("VIN", <span className="font-mono-data">{sale.vehicle?.vin}</span>)}
        {row("Engine No.", <span className="font-mono-data">{sale.vehicle?.engine_no}</span>)}
        {row("Delivery Date", sale.delivery?.promised_date)}
      </dl>
    </Card>
  );
}

const isFreeItem = (a) => (a.price || 0) === 0 && /free/i.test(a.name || "");

export default function SparesWorkspace() {
  const { saleId } = useParams();
  const navigate = useNavigate();
  const { activeUser, locations } = useApp();
  useTicker(60_000);

  const [sale, setSale] = useState(null);
  const [task, setTask] = useState(null);
  const [invoiceFiles, setInvoiceFiles] = useState([]);
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const seedItems = useCallback((s) => {
    // Source of truth: sale.spares.items if present, else billable accessories.
    if (s.spares?.items?.length) return s.spares.items.map((i) => ({ ...i }));
    return (s.accessory_items || [])
      .filter((a) => !isFreeItem(a))
      .map((a) => ({ name: a.name, price: a.price, original_price: a.price, status: "pending" }));
  }, []);

  const load = useCallback(async () => {
    const s = await Sales.get(saleId);
    setSale(s);
    const ts = await Tasks.list({ sale_id: saleId, type: "SPARES" });
    setTask(ts[0] || null);
    setInvoiceFiles(await Files.list(saleId, "spares_invoice"));
    setItems(seedItems(s));
    setNotes(s.spares?.notes || "");
  }, [saleId, seedItems]);

  useEffect(() => { load(); }, [load]);

  if (!sale) return <div className="py-10 text-center text-sm text-slate-500">Loading…</div>;

  const loc = locations.find((l) => l.id === sale.location_id);
  const isCompleted = task?.status === "completed";
  const readOnly = (activeUser?.role !== "Spares" && activeUser?.role !== "Admin") || isCompleted;
  const deliveryAt = buildDeliveryDateTime(sale.delivery?.promised_date, sale.delivery?.promised_time);

  const allDecided = items.length > 0 && items.every((i) => i.status === "assigned" || i.status === "not_available");
  const anyAssigned = items.some((i) => i.status === "assigned");
  const allNotAvailable = items.length > 0 && items.every((i) => i.status === "not_available");
  const canComplete = items.length > 0 && allDecided && (allNotAvailable || (anyAssigned && invoiceFiles.length > 0));

  const setStatus = (idx, val) => setItems(items.map((it, i) => i === idx ? { ...it, status: val } : it));
  const setPrice = (idx, val) => setItems(items.map((it, i) => i === idx ? { ...it, price: val === "" ? "" : Number(val) } : it));
  const setAll = (val) => setItems(items.map((it) => ({ ...it, status: val })));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateUpload("spares_invoice", file);
    if (err) { toast.error(err); e.target.value = ""; return; }
    setUploading(true);
    try {
      await Files.upload(saleId, "spares_invoice", file, activeUser?.id);
      toast.success("Invoice uploaded");
      setInvoiceFiles(await Files.list(saleId, "spares_invoice"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const onRemoveFile = async (fid) => {
    if (!window.confirm("Remove this invoice file?")) return;
    try {
      await Files.remove(fid);
      setInvoiceFiles(await Files.list(saleId, "spares_invoice"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const onAction = async (action) => {
    setBusy(true);
    try {
      const payload = { items, notes: notes || null, user_id: activeUser?.id };
      if (action === "complete") {
        await Spares.complete(saleId, payload);
        toast.success(allNotAvailable ? "Marked Not Available · task closed" : "Spares completed", { duration: 2500 });
        navigate("/spares");
      } else {
        await Spares.saveDraft(saleId, payload);
        toast.success("Draft saved");
        await load();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const overall = sale.spares?.overall_status;

  return (
    <div className="space-y-5" data-testid="spares-workspace-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <button onClick={() => navigate("/spares")}
            className="mb-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"
            data-testid="spares-back-btn">
            <ArrowLeft className="h-3 w-3" /> Back to Queue
          </button>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-3xl font-semibold tracking-tight">Accessories Fulfilment</h1>
            {isCompleted && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {overall === "not_available" ? "Not Available" :
                  overall === "partial" ? "Partial" : "Completed"}
              </span>
            )}
          </div>
          <div className="mt-1 text-sm text-slate-500">
            <Link to={`/sales/${sale.id}`} className="underline hover:text-primary">
              Open full sale detail
            </Link>
          </div>
        </div>
        <TimerBanner deliveryAt={deliveryAt} />
      </div>

      <ContextSection sale={sale} loc={loc} />

      <BillingLedgerBanner sale={sale} onUpdated={load} />

      <Card className="border-slate-200 p-5 sm:p-6" data-testid="spares-section">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">Accessories Requested</div>
            <p className="mt-1 text-xs text-slate-500">Mark each item as Assigned or Not Available.</p>
          </div>
          {!readOnly && items.length > 0 && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setAll("assigned")}
                className="gap-1.5 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="spares-mark-all-assigned">
                <CheckCircle2 className="h-3.5 w-3.5" /> All Assigned
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAll("not_available")}
                className="gap-1.5 border-rose-200 text-rose-700 hover:bg-rose-50"
                data-testid="spares-mark-all-na">
                <XCircle className="h-3.5 w-3.5" /> All Not Available
              </Button>
            </div>
          )}
        </div>

        {items.length === 0 ? (
          <div className="mt-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center">
            <Package className="mx-auto h-8 w-8 text-slate-300" />
            <p className="mt-2 text-sm text-slate-500">No paid accessories on this sale.</p>
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2">Item</th>
                  <th className="px-3 py-2 w-32 text-right">Price (Ref)</th>
                  <th className="px-3 py-2 w-56">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-white">
                {items.map((it, i) => {
                  const orig = it.original_price != null ? Number(it.original_price) : null;
                  const cur = it.price === "" || it.price == null ? null : Number(it.price);
                  const changed = orig != null && cur != null && orig !== cur;
                  return (
                  <tr key={`${it.name}-${i}`} className={cn(it.status === "not_available" && "bg-slate-50/60")}>
                    <td className="px-3 py-2 font-medium text-slate-900">{it.name}</td>
                    <td className="px-3 py-2 text-right">
                      <Input
                        type="number" min="0"
                        value={it.price ?? ""}
                        onChange={(e) => setPrice(i, e.target.value)}
                        disabled={readOnly || it.status === "not_available"}
                        className="h-8 text-right font-mono-data"
                        title="Reference price only — does not affect Payment / Accounts"
                        data-testid={`spares-item-price-${i}`}
                      />
                      {changed && (
                        <div
                          className="mt-1 text-right text-[10px] font-medium tracking-wider text-slate-500"
                          data-testid={`spares-item-price-audit-${i}`}
                          title={"Originally quoted at \u20B9" + orig.toLocaleString("en-IN")}
                        >
                          {"quoted \u20B9" + orig.toLocaleString("en-IN")}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Select value={it.status || "pending"}
                        onValueChange={(v) => setStatus(i, v)}
                        disabled={readOnly}>
                        <SelectTrigger data-testid={`spares-item-status-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="assigned">Assigned</SelectItem>
                          <SelectItem value="not_available">Not Available</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            {/* Reference total — captured by Spares for internal
                procurement tracking. Does NOT propagate to Payment /
                Accounts (Feb-2026 policy: Sales Form / Accounts owns the
                authoritative Accessories value). */}
            {(() => {
              const gross = items
                .filter((it) => it.status === "assigned")
                .reduce((s, it) => s + (Number(it.price) || 0), 0);
              const fmt = (n) => "\u20B9" + n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
              return (
                <div className="mt-3 space-y-1 rounded-md border border-slate-200 bg-slate-50/50 p-3 text-sm">
                  <div className="flex justify-between border-b border-slate-200 pb-1 font-semibold text-slate-900">
                    <span>Reference total (for Spares tracking only)</span>
                    <span className="font-mono-data" data-testid="spares-net-total">{fmt(gross)}</span>
                  </div>
                  <div className="pt-1 text-[11px] text-slate-500">
                    Prices entered here are for reference and procurement
                    tracking. The Payment → Accessories line and Net On-Road
                    Price are controlled by Sales / Accounts and are not
                    updated from this screen.
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </Card>

      {/* Invoice */}
      <Card className="border-slate-200 p-5 sm:p-6" data-testid="spares-invoice-section">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">Accessories Invoice</div>
        <p className="mt-2 text-xs text-slate-500">
          Invoice number not required. Mandatory only if any item is Assigned.
        </p>
        <div className="mt-4 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
          {invoiceFiles.length === 0 ? (
            <div className="text-center">
              <FileText className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-2 text-xs text-slate-500">No invoice uploaded yet</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {invoiceFiles.map((f) => (
                <li key={f.id} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 flex-shrink-0 text-slate-500" />
                    <span className="truncate text-sm text-slate-900">{f.original_filename || "invoice"}</span>
                    <span className="text-xs text-slate-400">({Math.round((f.size || 0) / 1024)} KB)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <a href={Files.url(f.id)} target="_blank" rel="noreferrer"
                      className="rounded-md border border-slate-200 p-1.5 text-slate-600 hover:bg-slate-50"
                      data-testid={`spares-invoice-preview-${f.id}`}>
                      <Eye className="h-3.5 w-3.5" />
                    </a>
                    {!readOnly && (
                      <button onClick={() => onRemoveFile(f.id)}
                        className="rounded-md border border-red-200 p-1.5 text-red-600 hover:bg-red-50"
                        data-testid={`spares-invoice-remove-${f.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {!readOnly && (
            <div className="mt-3">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                <Upload className="h-4 w-4" />
                {invoiceFiles.length ? "Replace file" : "Upload invoice"}
                <input type="file" accept={getUploadPolicy("spares_invoice").acceptAttr} onChange={onUpload}
                  disabled={uploading} className="hidden"
                  data-testid="spares-invoice-input" />
              </label>
              {uploading && <span className="ml-2 text-xs text-slate-500">Uploading…</span>}
            </div>
          )}
        </div>
        <div className="mt-4 space-y-1.5">
          <Label className="text-xs font-semibold">Notes</Label>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
            disabled={readOnly} data-testid="spares-notes" />
        </div>
      </Card>

      {!readOnly && (
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-end">
          {!canComplete && items.length > 0 && (
            <span className="flex items-center gap-1.5 text-xs text-amber-700">
              <AlertTriangle className="h-3.5 w-3.5" />
              {!allDecided
                ? "Mark every accessory Assigned or Not Available to continue."
                : "Upload invoice when any item is Assigned."}
            </span>
          )}
          <Button variant="outline" onClick={() => onAction("save")} disabled={busy} className="gap-1.5"
            data-testid="spares-save-btn">
            <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save Draft"}
          </Button>
          <Button onClick={() => onAction("complete")} disabled={busy || !canComplete}
            className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:hover:bg-slate-300"
            data-testid="spares-complete-btn">
            <FileCheck className="h-4 w-4" /> Mark Accessories Completed
          </Button>
        </div>
      )}
    </div>
  );
}
